import { PayloadAction } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import { call, put, takeLatest } from 'redux-saga/effects'

import history from '@/history'
import i18n from '@/i18n'
import { messageHandlerSet } from '@/redux/messageHandler/actions'
import * as DoctorAPI from '@/services/doctor'
import * as DoctorPatientAPI from '@/services/doctorPatient'
import * as PatientAPI from '@/services/patient'
import { AxiosCustomResponse } from '@/types'
import { createPassword } from '@/utils'
import { generateErrorMessage } from '@/utils/format'
import {
  addPatientFailed,
  addPatientRequest,
  addPatientSuccess,
  assignExistingPatientFailed,
  assignExistingPatientRequest,
  assignExistingPatientSuccess,
  getPatientRequest,
  getPatientsFailed,
  getPatientsRequest,
  getPatientsSuccess,
  getPatientSuccess,
  renewProgramRequest,
  renewProgramSuccess,
  stopManagePatientFailed,
  stopManagePatientRequest,
  stopManagePatientSuccess
} from './actions'
import {
  AddPatientRequestPayload,
  AssignExistingPatientRequestPayload,
  GetPatientRequestPayload,
  PatientsModel,
  RenewProgramRequestPayload,
  StopManagePatientRequestPayload
} from './types'

type AssignPatientReturnValue = {
  patientId: number
}

function * getPatientsSaga () {
  try {
    const patientsData: PatientsModel[] = yield call(
      DoctorAPI.getDoctorPatients
    )

    if (patientsData) {
      yield put(getPatientsSuccess({ patientsData }))
    } else {
      yield put(getPatientsFailed())
    }
  } catch (err) {
    console.log(err)
    yield put(getPatientsFailed())
  }
}

function * getPatientSaga ({ payload }: PayloadAction<GetPatientRequestPayload>) {
  try {
    const patientData: PatientsModel = yield call(DoctorAPI.getDoctorPatient, {
      patientId: payload.patientId
    })

    if (patientData) {
      yield put(getPatientSuccess({ patientData }))
    } else {
      yield put(getPatientsFailed())
    }
  } catch (err) {
    console.log(err)
    yield put(getPatientsFailed())
  }
}

function * addPatientError (errorMessage?: string) {
  yield put(
    messageHandlerSet({
      message: i18n.t('gui.notification.patient_registration_error_title'),
      description:
        errorMessage || i18n.t('gui.notification.error_generic_description'),
      type: 'error',
      notDismissable: true
    })
  )

  yield put(addPatientFailed())
}

function * renewProgramError (errorMessage?: string) {
  yield put(
    messageHandlerSet({
      message: i18n.t('gui.notification.renew_program_error_title'),
      description:
        errorMessage || i18n.t('gui.notification.error_generic_description'),
      type: 'error',
      notDismissable: true
    })
  )
}

function * assignExistingPatientError (err: AxiosError) {
  function alreadyAddedErrorMessage () {
    if (!err?.response?.data) return null
    const response = err.response as AxiosCustomResponse

    let msgVal = null
    response.data?.forEach(item => {
      if (item.code === 'already-added') {
        msgVal = item.details
      }
    })

    return msgVal
  }

  const alreadyAddedMsg = alreadyAddedErrorMessage()

  if (alreadyAddedMsg) {
    yield put(
      messageHandlerSet({
        message: i18n.t('gui.notification.login_error_title'),
        type: 'error',
        description: alreadyAddedMsg
      })
    )
  } else {
    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.patient_assignment_error'),
        type: 'error'
      })
    )
  }
  yield put(assignExistingPatientFailed())
}

function * assignExistingPatientSaga ({
  payload
}: PayloadAction<AssignExistingPatientRequestPayload>) {
  const { email } = payload

  try {
    const res: AssignPatientReturnValue = yield call(PatientAPI.assignPatient, {
      email
    })

    if (!res?.patientId) throw Error

    yield put(getPatientsRequest())

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.patient_assignment_title'),
        message: i18n.t('gui.notification.patient_assignment_description'),
        type: 'success'
      })
    )

    yield put(assignExistingPatientSuccess())

    history.replace('/patients')
  } catch (err) {
    yield call(assignExistingPatientError, err as AxiosError)
  }
}

function * addPatientSaga ({ payload }: PayloadAction<AddPatientRequestPayload>) {
  const {
    productId,
    fiscalCode,
    firstName,
    lastName,
    gender,
    dateOfBirth,
    username,
    lang,
    phoneNumber,
    otherContact
  } = payload

  const password = createPassword()

  const patientData = {
    productId,
    firstName,
    lastName,
    gender,
    dateOfBirth,
    username,
    password: password.natural,
    lang,
    fiscalCode,
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(otherContact ? { otherContact } : {})
  }

  try {
    const res: AxiosResponse = yield call(
      PatientAPI.addAndAssignPatient,
      patientData
    )

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.patient_registration'),
        type: 'success'
      })
    )

    yield put(addPatientSuccess())

    history.replace(`/patients/${res.data.patientId}`)
  } catch (err: any) {
    if (err.response.status === 400 && err.response?.data) {
      yield call(addPatientError, generateErrorMessage(err.response.data))

      return
    }

    yield call(addPatientError)
  }
}

function * renewProgramSaga ({
  payload
}: PayloadAction<RenewProgramRequestPayload>) {
  try {
    const patientData: PatientsModel = yield call(
      PatientAPI.renewTrainingProgram,
      payload
    )

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.program_renewed_successfully'),
        type: 'success'
      })
    )

    yield put(renewProgramSuccess())
    if (patientData) {
      yield put(getPatientSuccess({ patientData }))
    } else {
      yield put(getPatientsFailed())
    }

    // history.replace(`/patients/${res.data.patientId}`)
  } catch (err: any) {
    if (err.response.status === 400 && err.response?.data) {
      yield call(renewProgramError, generateErrorMessage(err.response.data))

      return
    }

    yield call(renewProgramError)
  }
}
function * stopManagePatientSaga ({
  payload
}: PayloadAction<StopManagePatientRequestPayload>) {
  const { redirect, patientId } = payload

  try {
    yield call(DoctorPatientAPI.stopManagePatient, {
      patientId
    })

    yield put(stopManagePatientSuccess({ patientId }))
    if (redirect) history.replace('/patients')
    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.patient_removed'),
        type: 'success'
      })
    )
  } catch (err) {
    console.log(err)
    yield put(stopManagePatientFailed())
  }
}

function * patientsSaga () {
  yield takeLatest(getPatientsRequest.type, getPatientsSaga)
  yield takeLatest(getPatientRequest.type, getPatientSaga)
  yield takeLatest(addPatientRequest.type, addPatientSaga)
  yield takeLatest(renewProgramRequest.type, renewProgramSaga)
  yield takeLatest(assignExistingPatientRequest.type, assignExistingPatientSaga)
  yield takeLatest(stopManagePatientRequest.type, stopManagePatientSaga)
}

export default patientsSaga
